// filepath: /Users/gero/Desktop/GitLab/servgest4/1/gestyFE/src/components/driver/modals/CarDetailsModal.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  XMarkIcon,
  CalendarIcon,
  ClockIcon,
  TruckIcon,
  DocumentTextIcon,
  InformationCircleIcon,
  KeyIcon,
  ReceiptPercentIcon
} from '@heroicons/react/24/outline';
import useApiClient from '../../../hooks/useApiClient';
import { useDriver } from '../../../contexts/DriverContext';
import TopUpModal from './TopUpModal';
import AddVehicleModal from '../AddVehicleModal';

const CarDetailsModal = ({ isOpen, onClose, selectedCar, onCarsChanged }) => {
  const { t } = useTranslation();
  // Store original body styles and scroll position
  const bodyStyles = useRef({});
  console.log(selectedCar, '?')

  // API client and state for autopass data
  const apiClient = useApiClient();
  const { driverAccount, refreshDriverAccount } = useDriver();
  const [autopassData, setAutopassData] = useState(null);
  const [loadingAutopass, setLoadingAutopass] = useState(false);
  const [autopassError, setAutopassError] = useState(null);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [unlockingData, setUnlockingData] = useState(false);
  const [showRefreshConfirmModal, setShowRefreshConfirmModal] = useState(false);
  const [showTakeOwnershipModal, setShowTakeOwnershipModal] = useState(false);

  // Create a robust scroll lock mechanism
  useEffect(() => {
    if (isOpen) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');

      // Store current scroll position and styles
      bodyStyles.current = {
        overflow: document.body.style.overflow,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        mainOverflow: mainElement?.style.overflow,
      };

      // Apply scroll lock styles
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.documentElement.style.overflow = 'hidden';

      // Also lock the main scrollable element
      if (mainElement) {
        mainElement.style.overflow = 'hidden';
      }
    } else if (bodyStyles.current.scrollY !== undefined) {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');

      // Restore original styles
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';

      // Restore main element overflow
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }

      // Restore scroll position
      if (mainElement) {
        mainElement.scrollTop = bodyStyles.current.scrollY;
      } else {
        window.scrollTo(0, bodyStyles.current.scrollY);
      }
    }

    return () => {
      // Find the main scrollable content area
      const mainElement = document.querySelector('main');

      // Always restore on unmount
      document.body.style.overflow = bodyStyles.current.overflow || '';
      document.body.style.position = bodyStyles.current.position || '';
      document.body.style.top = bodyStyles.current.top || '';
      document.body.style.width = bodyStyles.current.width || '';
      document.documentElement.style.overflow = '';

      // Restore main element
      if (mainElement && bodyStyles.current.mainOverflow !== undefined) {
        mainElement.style.overflow = bodyStyles.current.mainOverflow || '';
      }

      if (bodyStyles.current.scrollY !== undefined) {
        if (mainElement) {
          mainElement.scrollTop = bodyStyles.current.scrollY;
        } else {
          window.scrollTo(0, bodyStyles.current.scrollY);
        }
      }
    };
  }, [isOpen]);

  // Fetch autopass data when modal opens and car is selected
  useEffect(() => {
    const fetchAutopassData = async () => {
      if (!isOpen || !selectedCar?.id) {
        // Reset data when modal closes or no car selected
        setAutopassData(null);
        setAutopassError(null);
        setLoadingAutopass(false);
        return;
      }

      setLoadingAutopass(true);
      setAutopassError(null);

      try {
        const response = await apiClient.get(`/driver/car/autopass?carId=${selectedCar.id}`);
        setAutopassData(response);
      } catch (error) {
        console.error('Error fetching autopass data:', error);
        setAutopassError(t('carDetailsModal.errorLoadingData'));
      } finally {
        setLoadingAutopass(false);
      }
    };

    fetchAutopassData();
  }, [isOpen, selectedCar?.id, apiClient, t]);

  // Return null if the modal is not open
  if (!isOpen || !selectedCar) return null;

  // Function to check if ITP is expired or nearing expiration
  const getITPStatus = (itpDate) => {
    if (!itpDate) return { expired: false, nearing: false };
    const today = new Date();
    const expiryDate = new Date(itpDate);
    const diffTime = expiryDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return {
      expired: diffDays < 0,
      nearing: diffDays >= 0 && diffDays <= 30,
    };
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('ro-RO', options);
    } catch {
      return dateString;
    }
  };

  // Get status indicator color (dot)
  const getStatusIndicator = (status) => {
    switch (status?.toLowerCase()) {
      case 'activ': return 'bg-emerald-500';
      case 'in service': return 'bg-amber-500';
      case 'inactiv': return 'bg-rose-500';
      default: return 'bg-gray-400';
    }
  };

  // Get status text
  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'activ': return 'Activ';
      case 'in service': return 'În Service';
      case 'inactiv': return 'Inactiv';
      default: return status || 'Necunoscut';
    }
  };

  const itpStatus = getITPStatus(selectedCar.ITP);

  // Format kilometers with thousands separator
  const formatKm = (km) => {
    return km?.toLocaleString('ro-RO') || 'N/A';
  };

  // Format autopass codes
  const formatCodes = (codes) => {
    if (!codes || codes.length === 0) return 'N/A';
    return codes.join(', ');
  };

  // Handle unlocking more autopass data
  const handleUnlockData = async () => {
    const balance = driverAccount?.balance || 0;

    if (balance < 15) {
      setShowTopUpModal(true);
      return;
    }

    setUnlockingData(true);
    try {
      // Call API to update/unlock more data
      await apiClient.get(`/driver/car/autopass/update?carId=${selectedCar.id}`);

      // Refresh the autopass data
      const response = await apiClient.get(`/driver/car/autopass?carId=${selectedCar.id}`);
      setAutopassData(response);

      // Refresh driver account to update balance after payment
      await refreshDriverAccount();
    } catch (error) {
      console.error('Error unlocking autopass data:', error);
      setAutopassError(t('carDetailsModal.errorUnlockingData'));
    } finally {
      setUnlockingData(false);
    }
  };

  return (
    <div className="fixed inset-0 z-20 bg-slate-100/10 dark:bg-slate-900/10 backdrop-blur-sm flex justify-center items-center p-2">
      <div className="relative w-full max-w-5xl my-8 mb-20 max-h-svh md:max-h-[calc(80vh-2rem)] flex flex-col rounded-xl bg-white dark:bg-slate-800 shadow-2xl border dark:border-slate-700">
        {/* Header */}
        <div className="px-2 py-2 border-b border-slate-200 dark:border-slate-700 rounded-t-xl bg-purple-50 dark:bg-purple-900/20 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              <div className="p-1.5 sm:p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex-shrink-0">
                <TruckIcon className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white truncate">
                {t('carDetailsModal.title')} {selectedCar.plate}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 sm:p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-2 overflow-y-auto flex-1 max-h-[calc(80vh-10rem)]">
          <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
            {/* Vehicle Image */}
            {/* <div className="bg-slate-100 dark:bg-slate-700 rounded-lg h-64 flex items-center justify-center">
              <TruckIcon className="h-32 w-32 text-slate-400 dark:text-slate-500" />
            </div> */}

            {/* Vehicle Details */}
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="bg-white dark:bg-slate-700/50 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-600">
                <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-4 flex items-center">
                  <InformationCircleIcon className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                  {t('carDetailsModal.basicInfo')}
                </h3>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{t('carDetailsModal.make')}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedCar.make}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{t('carDetailsModal.model')}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedCar.model}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{t('carDetailsModal.year')}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedCar.year}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{t('carDetailsModal.engineType')}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedCar.engineType}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{t('carDetailsModal.engineCapacity')}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedCar.enginePower} {t('carDetailsModal.cubicCentimeters')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">{t('carDetailsModal.power')}</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white">{selectedCar.carDetails.enginePower} {t('carDetailsModal.kw')}</span>
                  </div>
                  {/* <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">Transmisie</span>
                    <span className="text-sm font-medium text-slate-900 dark:text-white lowercase">{selectedCar.transmissionType}</span>
                  </div> */}
                  <div className="flex justify-between">
                    <span className="text-sm text-slate-500 dark:text-slate-400">VIN</span>
                    <span className="text-sm font-mono text-slate-900 dark:text-white">{selectedCar.vin || 'N/A'}</span>
                  </div>
                  {/* <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Status</span>
                    <span className="flex items-center font-medium text-slate-800 dark:text-white">
                      <span className={`w-2.5 h-2.5 rounded-full mr-1.5 ${getStatusIndicator(selectedCar.status)}`}></span>
                      {getStatusText(selectedCar.status)}
                    </span>
                  </div> */}
                </div>
              </div>

              {/* ITP Information */}
            </div>
            <div className={`p-4 rounded-lg shadow-sm border ${itpStatus.expired ? 'bg-rose-50 dark:bg-rose-900/20 border-rose-200 dark:border-red-800' :
              itpStatus.nearing ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-yellow-800' :
                'bg-white dark:bg-slate-700/50 border-slate-200 dark:border-slate-600'
              }`}>
              <h3 className={`text-lg font-medium mb-4 flex items-center ${itpStatus.expired ? 'text-red-700 dark:text-red-300' :
                itpStatus.nearing ? 'text-yellow-700 dark:text-yellow-300' :
                  'text-slate-900 dark:text-white'
                }`}>
                <ClockIcon className="h-5 w-5 mr-2 text-purple-600 dark:text-purple-400" />
                {t('carDetailsModal.itpTitle')}
              </h3>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className={`text-sm ${itpStatus.expired ? 'text-rose-600 dark:text-rose-400' :
                    itpStatus.nearing ? 'text-amber-600 dark:text-amber-400' :
                      'text-slate-500 dark:text-slate-400'
                    }`}>{t('carDetailsModal.expiryDate')}</span>
                  <span className="text-sm font-medium text-slate-900 dark:text-white">{formatDate(selectedCar.ITP)}</span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm ${itpStatus.expired ? 'text-rose-600 dark:text-rose-400' :
                    itpStatus.nearing ? 'text-amber-600 dark:text-amber-400' :
                      'text-slate-500 dark:text-slate-400'
                    }`}>{t('carDetailsModal.status')}</span>
                  <span className={`text-sm font-medium ${itpStatus.expired ? 'text-rose-600 dark:text-rose-400' :
                    itpStatus.nearing ? 'text-amber-600 dark:text-amber-400' :
                      'text-emerald-600 dark:text-emerald-400'
                    }`}>
                    {itpStatus.expired ? t('carDetailsModal.expired') :
                      itpStatus.nearing ? t('carDetailsModal.expiringSoon') :
                        t('carDetailsModal.valid')}
                  </span>
                </div>
              </div>
            </div>

            {/* Take Ownership Section - Only show if owner is false */}
            {selectedCar.owner === false && (
              <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-blue-100 dark:from-blue-900/30 dark:via-indigo-900/30 dark:to-blue-800/30 p-6 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-600 relative overflow-hidden">
                {/* Background pattern */}
                <div className="absolute inset-0 opacity-5">
                  <div className="absolute inset-0" style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                  }}></div>
                </div>

                {/* Content */}
                <div className="relative z-10 text-center">
                  {/* Icon */}
                  <div className="flex justify-center mb-4">
                    <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-lg">
                      <KeyIcon className="w-6 h-6 text-white" />
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2">
                    {t('carDetailsModal.takeOwnershipTitle')}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-600 dark:text-slate-300 mb-4 max-w-md mx-auto leading-relaxed text-sm">
                    {t('carDetailsModal.takeOwnershipDescription')}
                  </p>

                  {/* Action Button */}
                  <button
                    onClick={() => setShowTakeOwnershipModal(true)}
                    className="inline-flex items-center justify-center px-6 py-3 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all transform hover:scale-105 active:scale-95"
                  >
                    <KeyIcon className="w-4 h-4 mr-2" />
                    {t('carDetailsModal.takeOwnershipButton')}
                  </button>

                  {/* Benefits */}
                  <div className="mt-4 text-xs text-blue-600 dark:text-blue-300">
                    <p>{t('carDetailsModal.ownershipBenefits')}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Autopass Interventions Section */}
            <div className="bg-white dark:bg-slate-700 p-2 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <h3 className="text-lg font-medium text-slate-800 dark:text-white mb-4 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-purple-500" />
                {t('carDetailsModal.autopassTitle')}
              </h3>

              {loadingAutopass ? (
                <div className="flex items-center justify-center py-">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                  <span className="ml-3 text-slate-600 dark:text-slate-400">{t('carDetailsModal.loadingData')}</span>
                </div>
              ) : autopassError ? (
                <div className="text-center py-8">
                  <div className="text-red-500 dark:text-red-400 mb-2">{autopassError}</div>
                  <button
                    onClick={() => {
                      setAutopassError(null);
                      // Trigger refetch by updating a dependency
                      const fetchAutopassData = async () => {
                        setLoadingAutopass(true);
                        try {
                          const response = await apiClient.get(`/driver/car/autopass?carId=${selectedCar.id}`);
                          setAutopassData(response);
                        } catch (error) {
                          setAutopassError(t('carDetailsModal.errorLoadingData'));
                        } finally {
                          setLoadingAutopass(false);
                        }
                      };
                      fetchAutopassData();
                    }}
                    className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 text-sm"
                  >
                    {t('carDetailsModal.tryAgain')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="space-y-4">
                    {autopassData?.autopassDataSnapshot?.interventions?.length > 0 ? (
                      autopassData.autopassDataSnapshot.interventions.map((intervention, index) => (
                        <div key={index} className="bg-slate-50 dark:bg-slate-600 p-2 rounded-md border border-slate-200 dark:border-slate-600">
                          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <div>
                              <span className="text-slate-500 dark:text-slate-400 text-sm">{t('carDetailsModal.date')}</span>
                              <div className="font-medium text-slate-800 dark:text-white">{formatDate(intervention.date)}</div>
                            </div>
                            <div>
                              <span className="text-slate-500 dark:text-slate-400 text-sm">{t('carDetailsModal.kilometers')}</span>
                              <div className="font-medium text-slate-800 dark:text-white">{formatKm(intervention.nrKm)} km</div>
                            </div>
                            <div>
                              <span className="text-slate-500 dark:text-slate-400 text-sm">{t('carDetailsModal.codes')}</span>
                              <div className="font-mono text-sm text-slate-800 dark:text-white break-all">{formatCodes(intervention.code)}</div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-slate-500 dark:text-slate-400 text-center py-4">
                        {t('carDetailsModal.noInterventions')}
                      </div>
                    )}

                    {/* Premium unlock section - show when lastRefreshDate is null */}
                    {autopassData?.lastRefreshDate === null && (
                      <div className="space-y-4">
                        {/* Premium unlock card - taller and more prominent */}
                        <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-purple-100 dark:from-purple-900/30 dark:via-indigo-900/30 dark:to-purple-800/30 p-8 rounded-xl border-2 border-dashed border-purple-300 dark:border-purple-600 relative overflow-hidden min-h-[240px]">
                          {/* Background pattern */}
                          <div className="absolute inset-0 opacity-5">
                            <div className="absolute inset-0" style={{
                              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                            }}></div>
                          </div>

                          {/* Content */}
                          <div className="relative z-10 text-center">
                            {/* Icon */}
                            <div className="flex justify-center mb-6">
                              <div className="p-4 bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full shadow-lg">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                </svg>
                              </div>
                            </div>

                            {/* Title */}
                            <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3">
                              {t('carDetailsModal.unlockTitle')}
                            </h3>

                            {/* Description */}
                            <p className="text-slate-600 dark:text-slate-300 mb-6 max-w-md mx-auto leading-relaxed">
                              {t('carDetailsModal.unlockDescription')} <span className="font-bold text-purple-600 dark:text-purple-400">15 RON</span>
                            </p>

                            {/* Action Button */}
                            <button
                              onClick={handleUnlockData}
                              disabled={unlockingData}
                              className={`inline-flex items-center justify-center px-8 py-4 rounded-xl text-lg font-semibold text-white transition-all transform ${unlockingData
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95'
                                }`}
                            >
                              {unlockingData ? (
                                <>
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                                  {t('carDetailsModal.processing')}
                                </>
                              ) : (
                                <>
                                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                  </svg>
                                  {t('carDetailsModal.unlockButton')}
                                </>
                              )}
                            </button>

                            {/* Balance indicator */}
                            <div className="mt-6">
                              <div className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium ${(driverAccount?.balance || 0) >= 15
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-700'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700'
                                }`}>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                </svg>
                                {t('carDetailsModal.currentBalance')}: {(driverAccount?.balance || 0).toFixed(2)} RON
                              </div>
                              {(driverAccount?.balance || 0) < 15 && (
                                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                                  {t('carDetailsModal.insufficientBalance')}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Benefits section */}
                        <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200 dark:border-amber-700">
                          <div className="flex items-start gap-4">
                            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex-shrink-0">
                              <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h4 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-3">
                                {t('carDetailsModal.fullAccessTitle')}
                              </h4>
                              <p className="text-sm text-amber-700 dark:text-amber-300 leading-relaxed mb-4">
                                {t('carDetailsModal.fullAccessDescription')}
                              </p>

                              {/* Feature list */}
                              <ul className="space-y-2 text-sm text-amber-700 dark:text-amber-300">
                                <li className="flex items-center">
                                  <svg className="w-4 h-4 mr-3 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  {t('carDetailsModal.feature1')}
                                </li>
                                <li className="flex items-center">
                                  <svg className="w-4 h-4 mr-3 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  {t('carDetailsModal.feature2')}
                                </li>
                                <li className="flex items-center">
                                  <svg className="w-4 h-4 mr-3 text-amber-600 dark:text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                  </svg>
                                  {t('carDetailsModal.feature3')}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Refresh data button - show when user already has data */}
                    {autopassData?.lastRefreshDate !== null && (
                      <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-1">
                              {t('carDetailsModal.refreshDataTitle')}
                            </h4>
                            <p className="text-xs text-blue-600 dark:text-blue-300 break-words">
                              {t('carDetailsModal.refreshDataDescription')} <span className="font-semibold">15 RON</span>
                            </p>
                          </div>
                          <button
                            onClick={() => {
                              if ((driverAccount?.balance || 0) < 15) {
                                setShowTopUpModal(true);
                              } else {
                                setShowRefreshConfirmModal(true);
                              }
                            }}
                            disabled={unlockingData}
                            className={`flex-shrink-0 inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium transition-colors min-w-[120px] ${unlockingData
                              ? 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 text-white shadow-sm hover:shadow-md'
                              }`}
                          >
                            {unlockingData ? (
                              <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
                                <span className="truncate">{t('carDetailsModal.refreshing')}</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                                <span className="truncate">{t('carDetailsModal.refreshData')}</span>
                              </>
                            )}
                          </button>
                        </div>
                        {(driverAccount?.balance || 0) < 15 && (
                          <div className="mt-3 flex items-start text-xs text-red-600 dark:text-red-400">
                            <svg className="w-4 h-4 mr-1 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <span className="break-words">{t('carDetailsModal.insufficientBalanceRefresh')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {autopassData?.autopassDataSnapshot && (
                    <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-600">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-slate-500 dark:text-slate-400">{t('carDetailsModal.registrationNumber')}:</span>
                        <span className="font-medium text-slate-800 dark:text-white">{autopassData.autopassDataSnapshot.registrationNumber}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm mt-1">
                        <span className="text-slate-500 dark:text-slate-400">{t('carDetailsModal.totalInterventions')}:</span>
                        <span className="font-medium text-slate-800 dark:text-white">{autopassData.autopassDataSnapshot.interventions?.length || 0}</span>
                      </div>
                      {autopassData.lastRefreshDate && (
                        <div className="flex justify-between items-center text-sm mt-1">
                          <span className="text-slate-500 dark:text-slate-400">{t('carDetailsModal.lastUpdate')}:</span>
                          <span className="font-medium text-slate-800 dark:text-white">{formatDate(autopassData.lastRefreshDate)}</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Additional Vehicle Information */}
          <div className="mt-6 grid grid-cols-3 md:grid-cols-1 gap-4">
            {/* Documents */}
            {/* <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <h3 className="text-md font-medium text-slate-800 dark:text-white mb-3 flex items-center">
                <DocumentTextIcon className="h-5 w-5 mr-2 text-purple-500" />
                Documente
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center text-slate-600 dark:text-slate-300">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                  Carte de identitate
                </div>
                <div className="flex items-center text-slate-600 dark:text-slate-300">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                  Talon
                </div>
                <div className="flex items-center text-slate-600 dark:text-slate-300">
                  <span className="w-2 h-2 bg-emerald-500 rounded-full mr-2"></span>
                  Asigurare RCA
                </div>
              </div>
            </div> */}

            {/* Insurance */}
            {/* <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <h3 className="text-md font-medium text-slate-800 dark:text-white mb-3 flex items-center">
                <ReceiptPercentIcon className="h-5 w-5 mr-2 text-purple-500" />
                Asigurări
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">RCA</span>
                  <span className="font-medium text-emerald-600 dark:text-emerald-400">Valid</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">CASCO</span>
                  <span className="font-medium text-slate-600 dark:text-slate-300">Nu există</span>
                </div>
              </div>
            </div> */}

            {/* Technical */}
            {/* <div className="bg-white dark:bg-slate-700 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
              <h3 className="text-md font-medium text-slate-800 dark:text-white mb-3 flex items-center">
                <WrenchIcon className="h-5 w-5 mr-2 text-purple-500" />
                Tehnic
              </h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">Ultima revizie</span>
                  <span className="font-medium text-slate-800 dark:text-white">15 Ian 2023</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-500 dark:text-slate-400">Următoare revizie</span>
                  <span className="font-medium text-slate-800 dark:text-white">15 Ian 2024</span>
                </div>
              </div>
            </div> */}
          </div>
        </div>

        {/* Footer/Close button */}
        <div className="px-3 sm:px-6 py-3 sm:py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 flex justify-end rounded-b-xl flex-shrink-0">
          <button
            type="button"
            className="w-full sm:w-auto bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 font-medium py-2 px-4 rounded-lg transition-colors"
            onClick={onClose}
          >
            {t('common.close')}
          </button>
        </div>
      </div>

      {/* TopUp Modal */}
      {showTopUpModal && (
        <TopUpModal
          isOpen={showTopUpModal}
          onClose={() => setShowTopUpModal(false)}
          onSuccess={async () => {
            setShowTopUpModal(false);
            // Refresh driver account to update balance after top-up
            await refreshDriverAccount();
            // After successful top-up, automatically try to unlock data
            setTimeout(() => {
              handleUnlockData();
            }, 1000); // Small delay to ensure balance is updated
          }}
        />
      )}

      {/* Take Ownership Modal */}
      {showTakeOwnershipModal && (
        <AddVehicleModal
          showAddCarModal={showTakeOwnershipModal}
          setShowAddCarModal={setShowTakeOwnershipModal}
          prefilledPlate={selectedCar.plate}
          carId={selectedCar.id}
          isOwnershipVerification={true}
          onCarsChanged={() => {
            // Refresh the car list in parent component if callback exists
            if (typeof onCarsChanged === 'function') {
              onCarsChanged();
            }
            // Close the car details modal after successful ownership transfer
            onClose();
          }}
        />
      )}

      {/* Refresh Confirmation Modal */}
      {showRefreshConfirmModal && (
        <div className="fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl border dark:border-slate-700 max-w-md w-full">
            {/* Header */}
            <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
                  {t('carDetailsModal.confirmRefreshTitle')}
                </h3>
                <button
                  onClick={() => setShowRefreshConfirmModal(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              <div className="text-center">
                {/* Icon */}
                <div className="flex justify-center mb-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                    <svg className="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </div>
                </div>

                {/* Message */}
                <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                  {t('carDetailsModal.confirmRefreshMessage')}
                </p>

                {/* Balance info */}
                <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 dark:text-slate-400">{t('carDetailsModal.currentBalance')}:</span>
                    <span className="font-medium text-slate-900 dark:text-white">{(driverAccount?.balance || 0).toFixed(2)} RON</span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-2">
                    <span className="text-slate-500 dark:text-slate-400">{t('carDetailsModal.refreshCost')}:</span>
                    <span className="font-medium text-red-600 dark:text-red-400">-15.00 RON</span>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-600 mt-2 pt-2">
                    <div className="flex justify-between items-center text-sm font-medium">
                      <span className="text-slate-700 dark:text-slate-300">{t('carDetailsModal.balanceAfter')}:</span>
                      <span className="text-slate-900 dark:text-white">{((driverAccount?.balance || 0) - 15).toFixed(2)} RON</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-700 rounded-b-xl">
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRefreshConfirmModal(false)}
                  className="flex-1 px-4 py-2 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-slate-100 dark:hover:bg-slate-600 border border-slate-300 dark:border-slate-600 rounded-lg font-medium transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => {
                    setShowRefreshConfirmModal(false);
                    handleUnlockData();
                  }}
                  disabled={unlockingData}
                  className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg font-medium shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {unlockingData ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                      {t('carDetailsModal.processing')}
                    </>
                  ) : (
                    <>
                      {t('carDetailsModal.confirmRefreshButton')} 15 RON
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CarDetailsModal;
